import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supervisorApi, adminApi, clientsApi, reportsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Users, FileText, ClipboardCheck, BarChart3, LogOut,
  CheckCircle2, AlertTriangle, Clock, Heart, Eye,
  ThumbsUp, MessageSquare, TrendingUp, Shield, ArrowRight,
  UserCheck, FileCheck
} from 'lucide-react';
import { MobileNav } from '../components/MobileNav';
import { SosBanner } from '../components/SosBanner';
import { LogoHome } from '../components/LogoHome';
import { LiveShiftsPanel } from '../components/LiveShiftsPanel';
import { MissedShiftsBanner } from '../components/MissedShiftsBanner';
import { TravelCompliancePanel } from '../components/TravelCompliancePanel';
import { MyUpcomingShiftsPanel } from '../components/MyUpcomingShiftsPanel';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function SupervisorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [teamStats, setTeamStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewFeedback, setReviewFeedback] = useState({});
  const [reviewingId, setReviewingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, workersRes, reviewsRes, clientsRes] = await Promise.all([
        supervisorApi.getTeamStats(),
        supervisorApi.getTeamWorkers(),
        supervisorApi.getPendingReviews(),
        clientsApi.getAll()
      ]);
      setTeamStats(statsRes.data);
      setWorkers(workersRes.data);
      setPendingReviews(reviewsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load supervisor data');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (reportId, action) => {
    setReviewingId(reportId);
    try {
      await supervisorApi.reviewReport(reportId, action, reviewFeedback[reportId] || '');
      toast.success(`Report ${action === 'approve' ? 'approved' : 'sent back for changes'}`);
      setPendingReviews(prev => prev.filter(r => r.id !== reportId));
      loadData();
    } catch (error) {
      toast.error('Failed to review report');
    } finally {
      setReviewingId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const supervisorNavItems = [
    { to: '/supervisor', label: 'Dashboard', icon: <ClipboardCheck className="h-5 w-5" />, active: true },
    { to: '/reports', label: 'Reports', icon: <FileText className="h-5 w-5" /> },
    { to: '/clients', label: 'Clients', icon: <Users className="h-5 w-5" /> },
    { to: '/compliance', label: 'Compliance', icon: <BarChart3 className="h-5 w-5" /> },
    { to: '/wellbeing', label: 'Team Wellbeing', icon: <Heart className="h-5 w-5" /> },
    { to: '/saved-pdfs', label: 'Saved PDFs', icon: <FileCheck className="h-5 w-5" /> },
    { to: '/analytics', label: 'Analytics', icon: <BarChart3 className="h-5 w-5 text-teal-400" /> },
    { to: '/team-chat', label: 'Team Chat', icon: <MessageSquare className="h-5 w-5" /> },
    { to: '/roster', label: 'Roster', icon: <Clock className="h-5 w-5 text-amber-400" /> },
    { to: '/team-allocations', label: 'Team Allocations', icon: <Users className="h-5 w-5 text-cyan-400" /> },
  ];

  return (
    <div className="min-h-screen bg-slate-900" data-testid="supervisor-dashboard">
      <MobileNav navItems={supervisorNavItems} user={user} onLogout={handleLogout} />
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-950 border-r border-slate-800 z-50 hidden lg:flex lg:flex-col">
        <div className="p-6 pb-2 flex-shrink-0">
          <LogoHome className="h-16 w-auto mb-4" />
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <nav className="space-y-1">
            <Link to="/supervisor" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
              <ClipboardCheck className="h-5 w-5" />
              Dashboard
            </Link>
            <Link to="/reports" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileText className="h-5 w-5" />
              All Reports
            </Link>
            <Link to="/clients" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Users className="h-5 w-5" />
              Clients
            </Link>
            <Link to="/compliance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="h-5 w-5" />
              Compliance
            </Link>
            <Link to="/wellbeing" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Heart className="h-5 w-5" />
              Team Wellbeing
            </Link>
            <Link to="/saved-pdfs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileCheck className="h-5 w-5" />
              Saved PDFs
            </Link>
            <Link to="/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="h-5 w-5 text-teal-400" />
              Analytics
            </Link>
            <Link to="/team-chat" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <MessageSquare className="h-5 w-5" />
              Team Chat
            </Link>
            <Link to="/roster" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="supervisor-roster-link">
              <Clock className="h-5 w-5 text-amber-400" />
              Roster
            </Link>
            <Link to="/team-allocations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="supervisor-team-allocations-link">
              <Users className="h-5 w-5 text-cyan-400" />
              Team Allocations
            </Link>
            <Link to="/timesheets" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="supervisor-timesheets-link">
              <Clock className="h-5 w-5 text-amber-400" />
              Timesheets
            </Link>
          </nav>
        </div>
        <div className="flex-shrink-0 p-6 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{user?.name}</p>
              <p className="text-slate-500 text-xs">Supervisor</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" onClick={handleLogout} data-testid="supervisor-logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64">
        {/* Mobile Header */}
        <header className="lg:hidden bg-slate-950 border-b border-slate-800 px-4 py-4 flex items-center justify-between">
          <LogoHome className="h-12 w-auto" />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400">
            <LogOut className="h-5 w-5" />
          </Button>
        </header>

        <div className="p-6 lg:p-8">
          <SosBanner />
          <MissedShiftsBanner />
          <div className="mb-8">
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">
              Supervisor Dashboard
            </h1>
            <p className="text-slate-400">Monitor team performance and review reports</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Team Workers</p>
                    <p className="text-2xl font-bold text-white">{teamStats?.total_workers || 0}</p>
                  </div>
                  <UserCheck className="h-8 w-8 text-teal-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Pending Reviews</p>
                    <p className="text-2xl font-bold text-amber-400">{teamStats?.submitted || 0}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Avg Compliance</p>
                    <p className="text-2xl font-bold text-green-400">{teamStats?.avg_compliance || 0}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Stress Alerts</p>
                    <p className="text-2xl font-bold text-red-400">{teamStats?.high_stress_alerts || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Live Shifts Panel — workers currently clocked in + live KMs */}
          <LiveShiftsPanel />

          {/* My upcoming shifts (supervisor's own) */}
          <MyUpcomingShiftsPanel userId={user?.id} />

          {/* Travel-Tracking Compliance Leaderboard */}
          <TravelCompliancePanel />

          {/* Tabs */}
          <Tabs defaultValue="reviews" className="space-y-6">
            <TabsList className="bg-slate-800 border-slate-700">
              <TabsTrigger value="reviews" className="data-[state=active]:bg-indigo-600" data-testid="tab-reviews">
                Pending Reviews ({pendingReviews.length})
              </TabsTrigger>
              <TabsTrigger value="team" className="data-[state=active]:bg-indigo-600" data-testid="tab-team">
                Team Overview
              </TabsTrigger>
              <TabsTrigger value="clients" className="data-[state=active]:bg-indigo-600" data-testid="tab-clients">
                Clients
              </TabsTrigger>
            </TabsList>

            {/* Pending Reviews Tab */}
            <TabsContent value="reviews">
              <div className="space-y-4">
                {pendingReviews.length === 0 ? (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-12 text-center">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                      <p className="text-white font-medium">All caught up!</p>
                      <p className="text-slate-400 text-sm mt-1">No reports pending review</p>
                    </CardContent>
                  </Card>
                ) : (
                  pendingReviews.map((report) => (
                    <Card key={report.id} className="bg-slate-800 border-slate-700" data-testid={`review-card-${report.id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-white font-medium capitalize">
                              {report.template_type?.replace(/_/g, ' ')}
                            </h3>
                            <div className="flex items-center gap-4 mt-1">
                              <span className="text-sm text-slate-400">Client: {report.client_name}</span>
                              <span className="text-sm text-slate-400">Worker: {report.worker_name}</span>
                              <span className="text-sm text-slate-500">
                                {new Date(report.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {report.compliance_score && (
                              <Badge className={report.compliance_score >= 85 ? 'bg-green-600' : report.compliance_score >= 70 ? 'bg-amber-600' : 'bg-red-600'}>
                                {report.compliance_score}% compliant
                              </Badge>
                            )}
                            <Link to={`/reports/${report.id}`}>
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                                <Eye className="h-4 w-4 mr-1" /> View Full
                              </Button>
                            </Link>
                          </div>
                        </div>

                        {report.ai_generated_report && (
                          <div className="bg-slate-900 rounded-lg p-4 mb-4 max-h-32 overflow-y-auto">
                            <p className="text-sm text-slate-300 whitespace-pre-line">
                              {report.ai_generated_report.substring(0, 400)}...
                            </p>
                          </div>
                        )}

                        <div className="space-y-3">
                          <Textarea
                            placeholder="Add feedback for the worker (optional)..."
                            value={reviewFeedback[report.id] || ''}
                            onChange={(e) => setReviewFeedback(prev => ({ ...prev, [report.id]: e.target.value }))}
                            className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 min-h-[60px]"
                            data-testid={`review-feedback-${report.id}`}
                          />
                          <div className="flex gap-3">
                            <Button
                              onClick={() => handleReview(report.id, 'approve')}
                              className="bg-green-600 hover:bg-green-700"
                              disabled={reviewingId === report.id}
                              data-testid={`approve-btn-${report.id}`}
                            >
                              <ThumbsUp className="h-4 w-4 mr-2" /> Approve
                            </Button>
                            <Button
                              onClick={() => handleReview(report.id, 'request_changes')}
                              variant="outline"
                              className="border-amber-600 text-amber-400 hover:bg-amber-600/10"
                              disabled={reviewingId === report.id}
                              data-testid={`request-changes-btn-${report.id}`}
                            >
                              <MessageSquare className="h-4 w-4 mr-2" /> Request Changes
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Team Overview Tab */}
            <TabsContent value="team">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Support Workers</CardTitle>
                  <CardDescription className="text-slate-400">Team performance and wellbeing overview</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">Reports</TableHead>
                        <TableHead className="text-slate-400">Submitted</TableHead>
                        <TableHead className="text-slate-400">Last Check-in</TableHead>
                        <TableHead className="text-slate-400">Wellbeing</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {workers.map((worker) => {
                        const checkin = worker.latest_checkin;
                        const stressLevel = checkin?.stress_level || 0;
                        return (
                          <TableRow key={worker.id} className="border-slate-700">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-teal-600 flex items-center justify-center text-white text-sm font-medium">
                                  {worker.name?.charAt(0)}
                                </div>
                                <div>
                                  <p className="text-white font-medium">{worker.name}</p>
                                  <p className="text-slate-500 text-xs">{worker.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-300">{worker.report_count}</TableCell>
                            <TableCell className="text-slate-300">{worker.submitted_count}</TableCell>
                            <TableCell className="text-slate-400 text-sm">
                              {checkin ? new Date(checkin.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' }) : 'Never'}
                            </TableCell>
                            <TableCell>
                              {checkin ? (
                                <Badge className={stressLevel >= 7 ? 'bg-red-600' : stressLevel >= 4 ? 'bg-amber-600' : 'bg-green-600'}>
                                  {stressLevel >= 7 ? 'High Stress' : stressLevel >= 4 ? 'Moderate' : 'Good'}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-slate-600 text-slate-400">No data</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {workers.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <Users className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                      <p>No support workers registered yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Clients Tab */}
            <TabsContent value="clients">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Client Overview</CardTitle>
                  <CardDescription className="text-slate-400">NDIS participants under your team's care</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">NDIS Number</TableHead>
                        <TableHead className="text-slate-400">Cultural Background</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow key={client.id} className="border-slate-700">
                          <TableCell className="text-white font-medium">{client.name}</TableCell>
                          <TableCell className="text-slate-300 font-mono text-sm">{client.ndis_number}</TableCell>
                          <TableCell className="text-slate-300">{client.cultural_background || '-'}</TableCell>
                          <TableCell>
                            <Link to={`/clients/${client.id}`}>
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">View</Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {clients.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <Users className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                      <p>No clients registered yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
